import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCircular, defaultValue } from "@/shared/model/edo-circular.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { EdoCircularVisibility } from "@/shared/model/enumerations/edo-circular-visibility.model";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { convertDateTimeToServer } from "@/shared/util/date-utils";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import { Paperclip, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Form,
  FormField,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DatePicker,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@/components/ui";
import axios from "axios";
import React, { createRef, useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";
import { Controller } from "react-hook-form";
import { z } from "zod";
import {
  createEntity,
  updateEntity,
} from "../../../shared/reducers/entities/edo-circular.reducer";
import { getEntities as getEdoClasseList } from "../../../shared/reducers/entities/edo-class.reducer";
import { getEntities as getEdoMateriaList } from "../../../shared/reducers/entities/edo-subject.reducer";

interface EditCircularProps {
  circular?: IEdoCircular;
  open: boolean;
  onClose: () => void;
}

const EditCircular = (props: EditCircularProps) => {
  const dispatch = useAppDispatch();
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoCircular>
  >((state) => state.edoCircular);
  const [form, setForm] = useState<IEdoCircular>({ ...defaultValue, ...props.circular });
  const fileInput = createRef<HTMLInputElement>();
  const [files, setFiles] = useState<File[]>([]);
  const isNew = !props.circular;
  const { circular, open, onClose: handleClose } = props;

  const handleCloseDialog = () => {
    setForm({ ...defaultValue });
    setFiles([]);
    handleClose();
  };

  useEffect(() => {
    dispatch(getEdoMateriaList({}));
    dispatch(getEdoClasseList({ page: 0, size: 1000, sort: `id:ASC` }));
  }, [dispatch]);

  useEffect(() => {
    setForm({ ...defaultValue, ...circular });
  }, [circular]);

  useEffect(() => {
    if (updateSuccess) handleCloseDialog();
  }, [updateSuccess]);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const saveEntity = async (data?: {
    title?: string;
    description?: string;
    visibility?: EdoCircularVisibility;
    date?: string;
  }) => {
    const payload = data ? { ...form, ...data } : form;
    if (isNew) {
      if (files.length) {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i], files[i].name);
        }
        const response = await axios.post<{ data: IEdoFile[] }>(
          "/files/upload",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        dispatch(
          createEntity({
            ...payload,
            date: convertDateTimeToServer(payload.date) as any,
            files: response.data.data.map((el) => ({ id: el.id })),
          } as any)
        );
      } else {
        dispatch(
          createEntity({
            ...payload,
            date: convertDateTimeToServer(payload.date) as any,
          } as any)
        );
      }
    } else {
      dispatch(
        updateEntity({
          ...payload,
          date: convertDateTimeToServer(payload.date) as any,
        } as any)
      );
    }
  };

  const schema = z.object({
    title: z.string().min(1, translate("edocendoApp.edoCircular.title") + " required"),
    description: z.string().min(1, translate("edocendoApp.edoCircular.description") + " required"),
    visibility: z.enum(["ALL", "STUDENTS", "TEACHERS"]),
    date: z.string().min(1, translate("edocendoApp.edoCircular.date") + " required"),
  });

  const selectFiles = () => fileInput.current?.click();

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(event.target.files ?? []));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCloseDialog()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? translate("edocendoApp.edoCircular.home.createLabel")
              : translate("edocendoApp.edoCircular.home.editLabel")}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <Form
            id="edit-circular-form"
            schema={schema}
            onSubmit={(data) => saveEntity(data)}
            defaultValues={{
              title: form.title ?? "",
              description: form.description ?? "",
              visibility: (form.visibility as "ALL" | "STUDENTS" | "TEACHERS") ?? "ALL",
              date: form.date ?? "",
            }}
            className="flex flex-col gap-4"
          >
            <FormField name="title" label={translate("edocendoApp.edoCircular.title")} />
            <Controller
              name="description"
              render={({ field }) => (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                    {translate("edocendoApp.edoCircular.description")}
                  </label>
                  <textarea
                    {...field}
                    rows={4}
                    className="flex w-full resize-y rounded-md border border-[var(--color-mockup-border)] bg-surface px-3 py-2 text-sm ring-offset-surface placeholder:text-[var(--color-mockup-text-light)] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:border-[var(--color-mockup-border)] dark:placeholder:text-[var(--color-mockup-text-light)] dark:focus:ring-primary"
                  />
                </div>
              )}
            />
            <Controller
              name="visibility"
              render={({ field }) => (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                    {translate("edocendoApp.edoCircular.visibility")}
                  </label>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={translate("edocendoApp.edoCircular.visibility")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">
                        <Translate contentKey="edocendoApp.EdoCircularVisibility.ALL">TUTTI</Translate>
                      </SelectItem>
                      <SelectItem value="STUDENTS">
                        <Translate contentKey="edocendoApp.EdoCircularVisibility.STUDENTS">STUDENTI</Translate>
                      </SelectItem>
                      <SelectItem value="TEACHERS">
                        <Translate contentKey="edocendoApp.EdoCircularVisibility.TEACHERS">DOCENTI</Translate>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <Controller
              name="date"
              render={({ field }) => (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                    {translate("edocendoApp.edoCircular.date")}
                  </label>
                  <DatePicker
                    selected={field.value ? new Date(field.value) : undefined}
                    onSelect={(d) => field.onChange(d?.toISOString())}
                    placeholder={translate("edocendoApp.edoCircular.date")}
                  />
                </div>
              )}
            />
            {isNew && (
              <>
                {files.length > 0 && (
                  <div className="py-2">
                    <List className="flex flex-col divide-y divide-[var(--color-mockup-border)]">
                      {files.map((file, index) => (
                        <ListItem key={index} className="flex items-center gap-2 py-2">
                          <div className="mr-3 shrink-0">{getFileAvatar(file.type)}</div>
                          <ListItemText primary={file.name} />
                          <ListItemSecondaryAction>
                            <IconButton
                              onClick={() => removeFile(index)}
                              aria-label="Rimuovi file"
                            >
                              <X className="h-4 w-4" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </div>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={selectFiles}
                  className="w-full"
                >
                  <Paperclip className="mr-2 h-4 w-4" />
                  Aggiungi file
                </Button>
                <input
                  type="file"
                  ref={fileInput}
                  name="files"
                  multiple
                  hidden
                  onChange={handleFiles}
                />
              </>
            )}
          </Form>
        )}
        <DialogFooter>
          <Button variant="outline-solid" onClick={handleCloseDialog}>
            <Translate contentKey="entity.action.cancel" />
          </Button>
          <Button
            type="submit"
            form="edit-circular-form"
            disabled={loading || updating}
          >
            {isNew ? translate("entity.action.create") : translate("entity.action.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCircular;
